<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxSorting,
  DxScrolling,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { BottoPagePanel, bottoGridPaging, getAuthUsersLookup, LookupBox } from '@botto/core';
import { setTransactionListFilters, transactionsStore } from '../api';

const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const authUsersLookup = getAuthUsersLookup();
const filterUserId = ref<number | null>(null);
const pageSizes = [10, 20, 50, 100];
const { paging, pager } = bottoGridPaging({ allowedPageSizes: pageSizes });

function userEmail(row: { user_id?: number | null }): string {
  return authUsersLookup.labelOf(row.user_id);
}

onMounted(async () => {
  await authUsersLookup.ensureLoaded();
  grid.value?.instance?.refresh();
});

watch(filterUserId, (v) => {
  setTransactionListFilters({ user_id: v });
  grid.value?.instance?.refresh();
});

function refresh(): void {
  grid.value?.instance?.refresh();
}
</script>

<template>
  <BottoPagePanel title="Транзакции" standalone>
    <template #actions>
      <LookupBox
        :model-value="filterUserId"
        :lookup="authUsersLookup"
        placeholder="Пользователь (email)"
        @update:model-value="(v) => (filterUserId = v as number | null)"
      />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <DxDataGrid
      ref="grid"
      :data-source="transactionsStore"
      key-expr="id"
      :remote-operations="{ paging: true, sorting: true, filtering: false }"
      :show-borders="false"
      :show-row-lines="false"
      :show-column-lines="false"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :paging="paging"
      :pager="pager"
      height="100%"
      width="100%"
    >
      <DxColumn data-field="id" caption="ID" data-type="number" :width="100" />
      <DxColumn data-field="timestamp" caption="Время" data-type="datetime" :width="180" />
      <DxColumn
        data-field="user_id"
        caption="Email"
        data-type="number"
        :width="220"
        :calculate-display-value="userEmail"
      />
      <DxColumn data-field="amount" caption="Сумма" data-type="number" :format="{ type: 'fixedPoint', precision: 2 }" />
      <DxColumn data-field="currency" caption="Валюта" :width="100" />
      <DxColumn data-field="client_amount" caption="Сумма (клиент)" data-type="number" :format="{ type: 'fixedPoint', precision: 2 }" />
      <DxColumn data-field="client_currency" caption="Валюта (клиент)" :width="140" />
      <DxColumn data-field="type" caption="Тип" data-type="number" :width="80" />
      <DxColumn data-field="comment" caption="Комментарий" />

      <DxSorting mode="single" />
      <DxScrolling mode="standard" />
    </DxDataGrid>
  </BottoPagePanel>
</template>
